import { useState } from 'react'
import { Send } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { usePublishPreview, usePublishProject } from '@/features/projects/use-publishes'
import type { ProjectDetail } from '@/api/types'

interface PublishButtonProps {
  project: ProjectDetail
}

/**
 * Phase 14 — primary "Publish" / secondary "Republish" button on the editor header. Click opens
 * a confirmation `<AlertDialog>` showing the next version + recipient count; confirm triggers
 * `usePublishProject`. PRIVATE projects refuse with a static body explaining the gate.
 */
export const PublishButton = ({ project }: PublishButtonProps) => {
  const [open, setOpen] = useState(false)
  const isPrivate = project.visibility === 'PRIVATE'
  const isPublished = project.isPublished === true || (project.publishedVersion ?? null) != null

  const preview = usePublishPreview(project.id, open && !isPrivate)
  const publish = usePublishProject()

  const handleConfirm = () => {
    publish.mutate(project.id, {
      onSuccess: () => {
        setOpen(false)
        toast.success(
          isPublished ? 'Republished' : `Published version ${preview.data?.nextVersion ?? 1}`
        )
      },
      onError: (err) => {
        const apiErr = err as { detail?: string }
        toast.error(apiErr.detail ?? 'Could not publish')
      },
    })
  }

  return (
    <>
      <Button
        variant={isPublished ? 'outline' : 'default'}
        size="sm"
        className="gap-2"
        onClick={() => setOpen(true)}
        disabled={publish.isPending}
      >
        <Send className="h-4 w-4" />
        {isPublished ? 'Republish' : 'Publish'}
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isPrivate
                ? 'This project is Private'
                : `Publish version ${preview.data?.nextVersion ?? (project.publishedVersion ?? 0) + 1}`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isPrivate
                ? 'Change Visibility to PUBLIC or UNLISTED before publishing — Private projects have no Subscribers to notify.'
                : preview.isPending
                  ? 'Calculating recipients…'
                  : `${preview.data?.recipientCount ?? 0} ${preview.data?.recipientCount === 1 ? 'Subscriber' : 'Subscribers'} will be notified.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={publish.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              disabled={isPrivate || publish.isPending || preview.isPending}
            >
              {publish.isPending ? 'Publishing…' : isPublished ? 'Republish' : 'Publish'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
